const toRGB = hex => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);

  return result
    ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)]
    : null;
};

export const toRGBA = (hex, alpha) => {
  const rgb = toRGB(hex);

  return rgb ? `rgba(${rgb.join(', ')}, ${alpha})` : null;
};

const COLORS_MAP = {
  ALICE_BLUE_LIGHT: '#e6f2fc',
  APPLE_2: '#149A53',
  AQUAMARINE: '#52C8F5',
  ASH_1: '#B8C6D9',
  ASH_2: '#4D515F',
  ATHENS_GRAY_1: '#e2e4eb',
  ATHENS_GRAY: '#e3e4eb',
  ATHENS_GREY: '#F7F7FA',
  BEIGE: '#fcf4e4',
  BITTERSWEET: '#DA5107',
  BLACK: '#000000',
  BRIGHT_GREEN: '#71DF96',
  BURNT_ORANGE: '#B36B00',
  BURNT_SIENNA: '#E66C43',
  BUTTERCUP: '#F5A623',
  CARNATION_PINK: '#FF98C3',
  CATALINA_BLUE: '#0B2585',
  CATSKILL_WHITE: '#E6EBF2',
  CHATEAU_GREEN: '#4CAC6A',
  CHELSEA_CUCUMBER: '#8EB355',
  CLOUD: '#F6F6F6',
  COD_GRAY: '#0B0B0B',
  COLA_LIGHTER: 'rgba(43, 43, 43, 0.75)',
  COLA: '#2B2B2B',
  CORAL: '#ff7f50',
  CORN: '#F4BF09',
  CORNSILK: '#FFF8DC',
  DARK_BLUE: '#003399',
  DARK_BLUE_2: '#01234a',
  DARK_FADED_GREY: '#757575',
  DARK_GRAY: '#7E7E7E',
  DARK_GREEN: '#459525',
  DARK_HEART: '#E85300',
  DARK_PINK: '#e75480',
  DARK_RED: '#8B0000',
  DEEP_KOAMARU: '#132C84',
  DISABLED_TEXT: '#999999',
  DISABLED: '#F2F2F2',
  DODGER_BLUE_2: '#1D7FFA',
  DODGER_BLUE_3: '#0ABBFF',
  DODGER_BLUE: '#2F77FF',
  DUSTY_GRAY: '#989898',
  EBONY_CLAY_1: '#222B32',
  EBONY_CLAY: '#222B32',
  ELECTRIC_PURPLE: '#a537fd',
  EMERALD: '#53ce5f',
  ENDEAVOUR: '#0567B4',
  ENERGY_YELLOW: '#F7D762',
  FADED_GREEN: '#e5fef8',
  FADED_YELLOW: '#fef8de',
  FLOWER_3: '#FCF0FF',
  FLOWER: '#DE5EE0',
  FOAM: '#EFF8FE',
  FRENCH_PASS: '#C0DCFE',
  FROSTED_MINT: '#E1FFF6',
  FUEL_YELLOW: '#EBB22F',
  FUNC_COLA: opacity => `rgba(0,0,0,${opacity})`,
  GALAXY_BLUE: '#2A4B7C',
  GERALDINE: '#F18FF3',
  GHOST: '#E0E0E0',
  GOBLIN: '#3d8745',
  GOLDEN_DREAM: '#ECC820',
  GRAY_BACK_DROP: 'rgba(52, 58, 67, 0.8)',
  GREEN_HAZE: '#03aa64',
  GREEN: '#2AC3AA',
  GREY_SHADE: '#bebebe',
  GREY93: '#ededed',
  HAVELOCK_BLUE_1: '#4A90E2',
  HAVELOCK_BLUE: '#5397dc',
  HIGHLIGHT: '#D6EAF8',
  LIGHT_BLUE: '#EFF4FF',
  LIGHT_ORANGE: '#FFD992',
  LIGHT_WHITE: '#EFF8FF',
  LINK_WATER_LIGHT: '#F2F6FB',
  LINK_WATER: '#DAE4F2',
  LINK: '#1EB5F0',
  MANATEE: '#929295',
  MIDDLE_SATURATED_ORANGE: '#FFB100',
  MIDNIGHT: '#001935',
  MILANO_RED: '#c22400',
  MINE_SHAFT: '#222222',
  MUSTARD: '#ffcf56',
  PALE_GREEN: '#90EE90',
  PERIWINKLE_GRAY_DARK: 'rgba(187, 193, 227, 0.6)',
  PIG_PINK: '#FFE9DD',
  PRIMARY_GRADIENT_LIGHT: 'linear-gradient(142.6deg, #0177FF 0%, #0D2FA9 100%)',
  PRIMARY: '#0A47C2',
  PURPLE: '#850F85',
  RED_SHADE: '#C22300',
  RED: '#EA5959',
  SCIENCE_BLUE: '#1233B2',
  SCORPION: 'rgba(43, 43, 43, 0.75)',
  SECONDARY: '#0A80D5',
  SHARK: '#2A2A2E',
  SPACE_CADET: '#223250',
  STROM_GRAY: '#71778B',
  TEXT_LIGHT: toRGBA('#000000', 0.4),
  TEXT_SECONDARY: toRGBA('#000000', 0.6),
  TITAN_WHITE: '#E8ECFF',
  TORCH_RED: '#E34C67',
  TROPICAL_RAIN_FOREST: '#005E2C',
  VARDEN: '#FCF4E4',
  WHITE_TRANSPARENT: 'rgba(255, 255, 255, 0.8)',
  WHITE: '#FFFFFF',
};

const GRAYS = {
  GRAY: '#2B2B2B',
  GRAY_DARK: '#292734',
  GRAY_LIGHT: '#808080',
  GREY_SHADOW: 'rgba(0, 0, 0, 0.12)',
  GRAY_XLIGHT: '#B3B3B3',
  GRAY_XXLIGHT: '#E0E0E0',
  GRAY_XXXLIGHT: '#F6F6F6',
};

const SUN = {
  SUN_XXLIGHT: '#FCF4E4',
  SUN_XLIGHT: '#FFDC83',
  SUN_LIGHT: '#FFCF56',
  SUN: '#FFBA07',
  SUN_DARK: '#D68100',
  SUN_XDARK: '#995200',
  SUN_3: '#FFF7DB',
};

const SKY = {
  SKY_XXLIGHT: '#ECF8FF',
  SKY_XLIGHT: '#8DD5F1',
  SKY_LIGHT: '#52C8F5',
  SKY: '#1EB5F0',
  SKY_DARK: '#0A80D5',
  SKY_XDARK: '#0A47C2',
  SKY_BLUE: '#01a5f2',
};

const GRASS = {
  GRASS_XXLIGHT: '#DDFFE4',
  GRASS_XLIGHT: '#AFE8C2',
  GRASS_LIGHT: '#71DF96',
  GRASS: '#3DD771',
  GRASS_DARK: '#149A53',
  GRASS_XDARK: '#005E2C',
};

const FLOWER = {
  FLOWER_XXLIGHT: '#FDEAFA',
  FLOWER_XLIGHT: '#EBBDEC',
  FLOWER_LIGHT: '#F985F9',
  FLOWER: '#ED6CEF',
  FLOWER_DARK: '#B038B2',
  FLOWER_XDARK: '#850F85',
};

const ORANGE = {
  ORANGE_XXLIGHT: '#FFE9DD',
  ORANGE_XLIGHT: '#FFC2A2',
  ORANGE_LIGHT: '#FF9F6C',
  ORANGE_MEDIUM: '#ffba07',
  ORANGE: '#FF7E3B',
  ORANGE_DARK: '#DA5107',
  ORANGE_XDARK: '#9E2F00',
};

const HEART = {
  HEART_XXLIGHT: '#FFEDF2',
  HEART_XLIGHT: '#FFD4E4',
  HEART_LIGHT: '#FF98C3',
  HEART: '#FF6782',
  HEART_DARK: '#E34C67',
  HEART_XDARK: '#BF2449',
};

const ASH = {
  ASH_XXLIGHT: '#E6EBF2',
  ASH_XLIGHT: '#DAE4F2',
  ASH_LIGHT: '#B8C6D9',
  ASH: '#858DA6',
  ASH_DARK: '#71778B',
  ASH_XDARK: '#4D515F',
};

const BACKGROUNDS = {
  BG_MILK: '#FFFFFF',
  BG_CLOUD: '#F6F6F6',
  BG_GHOST: '#E0E0E0',
  BG_MODAL: toRGBA('#F6F6F6', 0.9),
  BG_MILK_LIGHT: toRGBA('#FFFFFF', 0.3),
};

const COLORS = {
  ...COLORS_MAP,
  TEXT_REGULAR: GRAYS.GRAY,
  TEXT_LIGHT: toRGBA(GRAYS.GRAY, 0.75),
  TEXT_LIGHTER: GRAYS.GRAY_XLIGHT,
  BORDER: COLORS_MAP.TITAN_WHITE,
  SUB_TEXT: toRGBA(COLORS_MAP.WHITE, 0.7),
  OVERLAY: toRGBA(COLORS_MAP.MINE_SHAFT, 0.8),
  DARK_OVERLAY: 'rgba(28,43,60,0.95)',
  BG_CLOUD_LIGHT: toRGBA(BACKGROUNDS.BG_CLOUD, 0.8),
  ...GRAYS,
  ...SUN,
  ...SKY,
  ...GRASS,
  ...FLOWER,
  ...ORANGE,
  ...HEART,
  ...ASH,
  ...BACKGROUNDS,
};

export default COLORS;
